import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { getIntakeForm } from '../services/intakeFormService';
import Badge from '../components/common/Badge';
import Spinner from '../components/common/Spinner';
import Modal from '../components/common/Modal';
import IntakeFormBuilder from '../components/features/intakeForms/IntakeFormBuilder';
import AssignFormModal from '../components/features/intakeForms/AssignFormModal';

export default function IntakeFormDetail() {
  const { id } = useParams();
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['intakeForm', id],
    queryFn: () => getIntakeForm(id),
  });

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (isError) return <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load form.</div>;

  const { form } = data;
  const submitted = form.responses?.filter((r) => r.submittedAt) ?? [];
  const pending = form.responses?.filter((r) => !r.submittedAt) ?? [];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/intake-forms" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{form.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge label={form.isActive ? 'Active' : 'Inactive'} variant={form.isActive ? 'success' : 'default'} />
              <span className="text-sm text-neutral-500 dark:text-neutral-400">· {form.schema?.length ?? 0} questions</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setIsAssignOpen(true)} className="btn btn-secondary text-sm">Assign to client</button>
          <button onClick={() => setIsBuilderOpen(true)} className="btn btn-secondary text-sm">Edit form</button>
        </div>
      </div>

      {form.description && (
        <div className="card mb-6 text-sm text-neutral-700 dark:text-neutral-300">{form.description}</div>
      )}

      {/* Questions preview */}
      <div className="card mb-6">
        <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Questions</h2>
        <ol className="space-y-2 list-decimal list-inside">
          {(form.schema || []).map((q) => (
            <li key={q.id} className="text-sm text-neutral-700 dark:text-neutral-300">
              {q.label}
              {q.required && <span className="text-danger-500 ml-1">*</span>}
              <span className="text-xs text-neutral-400 dark:text-neutral-500 ml-2">({q.type})</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Responses */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            Responses ({submitted.length} submitted, {pending.length} pending)
          </h2>
        </div>
        {form.responses?.length === 0 ? (
          <div className="px-6 py-8 text-sm text-neutral-500 dark:text-neutral-400 text-center">No responses yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Client</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Submitted</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Signed</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {form.responses.map((r) => (
                <tr key={r.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                  <td className="px-6 py-4 text-neutral-700 dark:text-neutral-300">
                    {r.client ? `${r.client.firstName} ${r.client.lastName}` : '—'}
                  </td>
                  <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                    {r.submittedAt ? format(new Date(r.submittedAt), 'MMM d, yyyy') : (
                      <span className="text-yellow-600 dark:text-yellow-400">Pending</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <Badge label={r.signedAt ? 'Signed' : 'Unsigned'} variant={r.signedAt ? 'success' : 'default'} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link to={`/intake-forms/responses/${r.id}`} className="text-primary-600 hover:underline text-sm">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        title="Edit form"
        size="xl"
      >
        <IntakeFormBuilder existingForm={form} onClose={() => setIsBuilderOpen(false)} />
      </Modal>

      <AssignFormModal
        formId={form.id}
        formTitle={form.title}
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
      />
    </div>
  );
}
